import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, X } from 'lucide-react';

const MONTHS = [
  { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
  { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
  { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
  { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
];
const NOW = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => NOW - i);
const FIN_YEARS = Array.from({ length: 10 }, (_, i) => NOW + 1 - i);

const PERIOD_TYPES = [
  { value: 'all', label: 'All Time' },
  { value: 'last_12_months', label: 'Last 12 Months' },
  { value: 'financial_year', label: 'Fin. Year' },
  { value: 'year', label: 'Year' },
  { value: 'month', label: 'Month' },
];

const MORTGAGE_CATEGORIES = new Set([
  'Transfer - ANZ Offset',
  'Transfer - CBA',
  'Transfer - ME Transaction',
]);
const MORTGAGE_LABEL = 'Mortgage Repayments';

const SHOPPING_CATEGORIES = new Set([
  'Dan Shopping',
  'Sarah Shopping',
  'Online Shopping',
  'Homewares',
]);
const SHOPPING_LABEL = 'Shopping';

export default function FilterBar({ filters, setFilters, allCategories }) {
  // Collapse grouped sub-categories into a single display entry
  const displayCategories = [
    ...allCategories.filter(c => !MORTGAGE_CATEGORIES.has(c) && !SHOPPING_CATEGORIES.has(c)),
    ...(allCategories.some(c => MORTGAGE_CATEGORIES.has(c)) ? [MORTGAGE_LABEL] : []),
    ...(allCategories.some(c => SHOPPING_CATEGORIES.has(c)) ? [SHOPPING_LABEL] : []),
  ].sort();

  const toggle = (cat) => {
    if (cat === MORTGAGE_LABEL) {
      const has = filters.categories.includes(MORTGAGE_LABEL);
      const without = filters.categories.filter(c => c !== MORTGAGE_LABEL);
      setFilters({ ...filters, categories: has ? without : [...without, MORTGAGE_LABEL] });
    } else if (cat === SHOPPING_LABEL) {
      const has = filters.categories.includes(SHOPPING_LABEL);
      const without = filters.categories.filter(c => c !== SHOPPING_LABEL);
      setFilters({ ...filters, categories: has ? without : [...without, SHOPPING_LABEL] });
    } else {
      const cats = filters.categories;
      setFilters({ ...filters, categories: cats.includes(cat) ? cats.filter(c => c !== cat) : [...cats, cat] });
    }
  };

  return (
    <Card className="border-0 shadow-sm bg-white">
      <CardContent className="p-4">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Period Type Pills */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {PERIOD_TYPES.map(pt => (
              <button
                key={pt.value}
                onClick={() => setFilters({ ...filters, periodType: pt.value })}
                className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
                  filters.periodType === pt.value
                    ? 'bg-white text-emerald-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {pt.label}
              </button>
            ))}
          </div>

          {/* Month picker */}
          {filters.periodType === 'month' && (
            <div className="flex gap-2">
              <Select value={String(filters.month)} onValueChange={v => setFilters({ ...filters, month: parseInt(v) })}>
                <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map(m => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={String(filters.year)} onValueChange={v => setFilters({ ...filters, year: parseInt(v) })}>
                <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Year picker */}
          {filters.periodType === 'year' && (
            <Select value={String(filters.year)} onValueChange={v => setFilters({ ...filters, year: parseInt(v) })}>
              <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          {/* Financial Year picker */}
          {filters.periodType === 'financial_year' && (
            <Select value={String(filters.financialYear)} onValueChange={v => setFilters({ ...filters, financialYear: parseInt(v) })}>
              <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIN_YEARS.map(y => (
                  <SelectItem key={y} value={String(y)}>FY {y - 1}–{String(y).slice(2)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Category filter */}
          {allCategories.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
                  Categories
                  {filters.categories.length > 0 && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0 h-4">
                      {filters.categories.length}
                    </Badge>
                  )}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-52 p-2" align="start">
                <div className="space-y-0.5 max-h-56 overflow-y-auto">
                  {displayCategories.map(cat => (
                    <label key={cat} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer">
                      <Checkbox checked={filters.categories.includes(cat)} onCheckedChange={() => toggle(cat)} />
                      <span className="text-sm text-slate-700">{cat}</span>
                    </label>
                  ))}
                </div>
                {filters.categories.length > 0 && (
                  <Button variant="ghost" size="sm" className="w-full mt-2 text-xs h-7"
                    onClick={() => setFilters({ ...filters, categories: [] })}>
                    Clear all
                  </Button>
                )}
              </PopoverContent>
            </Popover>
          )}

          {/* Active category badges */}
          <div className="flex flex-wrap gap-1">
            {filters.categories.map(cat => (
              <Badge key={cat} variant="outline" className="text-xs gap-1 h-6 pl-2 pr-1.5">
                {cat}
                <button onClick={() => toggle(cat)}><X className="w-3 h-3" /></button>
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}